import { useEffect, useRef } from 'react';
import { Cat, KINDS } from '../../gameplay/registry';
import { Terrain } from '../../world/maps/mapTypes';
import { FX_ONE } from '../../engine/simulation/fixed';
import type { GameClient } from '../../app/gameClient';

const TERRAIN_RGB: Record<number, [number, number, number]> = {
  [Terrain.Grass]: [96, 128, 52],
  [Terrain.Dirt]: [128, 100, 66],
  [Terrain.Sand]: [208, 186, 132],
  [Terrain.Rock]: [118, 112, 104],
  [Terrain.Snow]: [226, 232, 240],
  [Terrain.Shallow]: [70, 130, 140],
  [Terrain.Water]: [30, 72, 110],
  [Terrain.Forest]: [60, 84, 36],
  [Terrain.Road]: [150, 124, 88],
};

/**
 * Minimap: terrain (with hill shading), fog of war, resources, units and
 * buildings in team colours, and the camera view. Click or drag to move the
 * camera; right-click to order selected units.
 */
export function Minimap({ client }: { client: GameClient }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = ref.current!;
    const ctx = canvas.getContext('2d')!;
    const w = client.sim.world;
    const map = w.map;
    const size = map.size;
    canvas.width = size;
    canvas.height = size;
    const base = ctx.createImageData(size, size);
    const vs = size + 1;
    for (let y = 0; y < size; y++)
      for (let x = 0; x < size; x++) {
        const i = y * size + x;
        const c = TERRAIN_RGB[map.terrain[i]] ?? [90, 90, 90];
        const h = map.heights[y * vs + x];
        const hx = map.heights[y * vs + x + 1] - h;
        const shade = Math.max(0.65, Math.min(1.3, 1 + (h / 600) * 0.4 - hx / 80));
        base.data[i * 4] = c[0] * shade;
        base.data[i * 4 + 1] = c[1] * shade;
        base.data[i * 4 + 2] = c[2] * shade;
        base.data[i * 4 + 3] = 255;
      }
    const frame = ctx.createImageData(size, size);
    const colors = w.players.map((p) => [(p.color >> 16) & 255, (p.color >> 8) & 255, p.color & 255]);
    let raf = 0;
    let last = 0;
    const draw = (t: number) => {
      raf = requestAnimationFrame(draw);
      if (t - last < 200) return;
      last = t;
      const viewer = client.local;
      const vis = viewer >= 0 && !client.renderer.revealAll ? w.visibility[viewer] : null;
      const d = frame.data;
      const b = base.data;
      for (let i = 0; i < size * size; i++) {
        const v = vis ? vis[i] : 2;
        const k = v === 2 ? 1 : v === 1 ? 0.5 : 0;
        d[i * 4] = b[i * 4] * k;
        d[i * 4 + 1] = b[i * 4 + 1] * k;
        d[i * 4 + 2] = b[i * 4 + 2] * k;
        d[i * 4 + 3] = 255;
      }
      const put = (x: number, y: number, r: number, g: number, bl: number, sz: number) => {
        for (let yy = y - (sz >> 1); yy <= y + (sz >> 1); yy++)
          for (let xx = x - (sz >> 1); xx <= x + (sz >> 1); xx++) {
            if (xx < 0 || yy < 0 || xx >= size || yy >= size) continue;
            const j = (yy * size + xx) * 4;
            d[j] = r;
            d[j + 1] = g;
            d[j + 2] = bl;
          }
      };
      for (let s = 0; s < w.highSlot; s++) {
        if (!w.alive[s] || w.cat[s] === Cat.Held) continue;
        const x = Math.floor(w.x[s] / FX_ONE);
        const y = Math.floor(w.y[s] / FX_ONE);
        const v = vis ? vis[y * size + x] : 2;
        const own = w.owner[s] === viewer;
        if (w.cat[s] === Cat.Resource) {
          if (v === 0) continue;
          const r = KINDS[w.kind[s]].resource!;
          if (r.model === 'gold') put(x, y, 250, 210, 60, 1);
          else if (r.model === 'stone') put(x, y, 200, 200, 196, 1);
          else if (r.model === 'berry' || r.model === 'deer' || r.model === 'sheep') put(x, y, 220, 90, 90, 1);
          else put(x, y, 36, 62, 26, 1);
          continue;
        }
        if (w.cat[s] === Cat.Unit && !own && v !== 2) continue;
        if (w.cat[s] === Cat.Building && !own && v !== 2) continue; // drawn from memory below
        const c = colors[w.owner[s]] ?? [255, 255, 255];
        const sel = client.selection.has(w.idOf(s));
        if (w.cat[s] === Cat.Building) put(x, y, c[0], c[1], c[2], KINDS[w.kind[s]].building!.size >= 3 ? 3 : 2);
        else put(x, y, sel ? 255 : c[0], sel ? 255 : c[1], sel ? 255 : c[2], 1);
      }
      // Enemy buildings outside vision appear only where they were last seen.
      if (vis) {
        for (const m of client.renderer.memory.values()) {
          const x = Math.floor(m.x);
          const y = Math.floor(m.z);
          if (vis[y * size + x] === 2) continue;
          const c = colors[m.owner] ?? [255, 255, 255];
          put(x, y, c[0], c[1], c[2], KINDS[m.kind].building!.size >= 3 ? 3 : 2);
        }
      }
      ctx.putImageData(frame, 0, 0);
      // Territorial control points.
      for (const pt of client.sim.victoryState.points) {
        const owner = w.players.find((pl) => pl.team === pt.team);
        ctx.fillStyle = owner ? '#' + owner.color.toString(16).padStart(6, '0') : '#ffffff';
        ctx.strokeStyle = '#000';
        ctx.beginPath();
        ctx.arc(pt.x / FX_ONE, pt.y / FX_ONE, 2.5, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
      }
      // Camera view polygon.
      const pts = client.cam.groundFrustum((nx, ny) => client.renderer.groundAtNdc(nx, ny));
      if (pts.length >= 3) {
        ctx.strokeStyle = 'rgba(255,255,255,0.9)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        pts.forEach((p, i) => (i ? ctx.lineTo(p.x, p.z) : ctx.moveTo(p.x, p.z)));
        ctx.closePath();
        ctx.stroke();
      }
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [client]);

  const toWorld = (e: React.MouseEvent) => {
    const r = ref.current!.getBoundingClientRect();
    const size = client.sim.map.size;
    return [((e.clientX - r.left) / r.width) * size, ((e.clientY - r.top) / r.height) * size] as const;
  };
  return (
    <div className="panel minimap">
      <canvas
        ref={ref}
        onMouseDown={(e) => {
          const [x, z] = toWorld(e);
          if (e.button === 2) client.rightClick(x, z, -1, e.shiftKey);
          else client.cam.jumpTo(x, z);
        }}
        onMouseMove={(e) => {
          if (e.buttons & 1) {
            const [x, z] = toWorld(e);
            client.cam.jumpTo(x, z);
          }
        }}
        onContextMenu={(e) => e.preventDefault()}
        aria-label="Minimap"
      />
    </div>
  );
}
